/**
 * Garment parts that break a figure's outline (lane C0-OUTFIT; research docs/research/C0_OUTFITS.md §6), worn by the
 * fashion outfits (rig/fashion.ts):
 * - `flyScarf`: a long flying scarf, two tails trailing behind the figure and fluttering with its motion;
 * - `drape`: a cape's shoulder drape — a yoke over the shoulders and a panel over each upper arm — so a cape reads
 *   from the front as well as from behind;
 * - `bellSleeves`: wide hanging sleeves under each forearm, flaring toward the cuff and swinging out on strikes.
 * They trail by the frame's sway (figure.ts `flutter`) and the cloth pass (rig/cloth.ts).
 */
import { shade } from '../palette';
import { flutter, type Layer, type Piece, px, torsoWidths, zOf } from './figure';
import type { Side } from './skeleton';
import { clothOf } from './cloth';

type G = CanvasRenderingContext2D;

export type Extra =
  | { id: 'scarf'; colour: string; len?: number }
  | { id: 'drape'; colour: string; len?: number; edge?: string; clasp?: string }
  | { id: 'sleeves'; colour: string; trim: string; len?: number };

export const EXTRA_LABELS: Record<Extra['id'], string> = {
  scarf: 'ผ้าพันคอยาวปลิว',
  drape: 'ผ้าคลุมพาดบ่า',
  sleeves: 'แขนเสื้อกว้างห้อย',
};

const SIDES: readonly Side[] = ['A', 'B'];

// ── flying scarf ──

function flyScarf(e: Extra & { id: 'scarf' }): Layer {
  const s = shade(e.colour, -0.13);
  const len = e.len ?? 30;
  return {
    id: 'flow:scarf',
    label: EXTRA_LABELS.scarf,
    pieces(sk) {
      const j = sk.joints;
      const top = Math.round(j.chest.y) - 2;
      // How much the tails fly (0 = hanging, 1 = streaming out behind): by the gait and the frame's sway, so at rest
      // they hang down the back with gravity (ns-art STARTER-COLOURS fix 1: level tails read as stiff red claws).
      const gait = sk.pose === 'sprint' ? 1 : sk.pose === 'walk' ? 0.6 : 0;
      const blow = Math.max(gait, Math.min(1, Math.max(0, (Math.abs(sk.rig.sway) - 1) * 0.35)));
      const tail = (g: G, n0: number, drop: number, off: number) => {
        // Trailing behind (the figure faces +x when turned); head-on, out to one side and down.
        const dir = sk.square ? (sk.back ? -1 : 1) : -1;
        const x0 = j.neck.x + (sk.square ? dir * 5 : -2);
        // Hanging, the tail falls straight from the shoulder and shows less of its length (to about the hips).
        const n = Math.max(4, Math.round(n0 * (0.55 + 0.45 * blow)));
        const step = (sk.square ? 0.8 : 0.75) * blow + 0.12 * (1 - blow);
        const fall = drop * blow + 1 * (1 - blow);
        for (let i = 0; i <= n; i++) {
          const k = i / n;
          const { dx, dy } = flutter(sk, k, i, 0.4 + 1.2 * blow);
          const x = x0 + dir * i * step + dx;
          const y = top + off + i * fall + dy;
          const w = k < 0.6 ? 3 : 2;
          const c = i >= n - 1 || i % 5 === 4 ? s : e.colour;
          px(g, x - 1, y, w, 1, c);
          // The underside a pixel darker (the side away from the light, screen-left).
          px(g, x - 1, y, 1, 1, s);
        }
      };
      const tails = (g: G) => {
        tail(g, len, sk.square ? 0.6 : 0.5, 1);
        tail(g, Math.round(len * 0.7), sk.square ? 0.75 : 0.7, 2);
      };
      return [
        { z: sk.back ? zOf(sk, 'chest', 0.09) : -0.97, draw: tails },
        {
          // The wrap round the neck and its knot, in front.
          z: zOf(sk, 'chest', 0.09),
          draw: (g) => {
            const nx = Math.round(j.neck.x);
            px(g, nx - 3, top - 1, 7, 2, e.colour);
            px(g, nx - 3, top + 1, 7, 1, s);
            if (!sk.back) px(g, nx + (sk.square ? 1 : -2), top + 1, 2, 2, s);
          },
        },
      ];
    },
  };
}

// ── shoulder drape ──

function drape(e: Extra & { id: 'drape' }): Layer {
  const s = shade(e.colour, -0.13);
  const len = e.len ?? 11;
  return {
    id: 'flow:drape',
    label: EXTRA_LABELS.drape,
    pieces(sk) {
      const j = sk.joints;
      const [shw] = torsoWidths(sk);
      const out: Piece[] = [];
      const yoke = Math.round(j.chest.y) - 1;
      out.push({
        z: zOf(sk, 'chest', 0.1),
        draw: (g) => {
          const cx = j.chest.x;
          const hw = Math.round(shw) + 1;
          for (let x = Math.round(cx - hw); x <= Math.round(cx + hw); x++) {
            // An opening at the throat, toward the camera.
            if (!sk.back && Math.abs(x - cx - (sk.square ? 0 : 1)) < 1.5) continue;
            px(g, x, yoke, 1, 2, e.colour);
            px(g, x, yoke + 2, 1, 1, s);
          }
          if (!sk.back && e.clasp) px(g, Math.round(cx + (sk.square ? 0 : 1)) - 1, yoke + 1, 2, 2, e.clasp);
        },
      });
      for (const side of SIDES) {
        const sh = j[`shoulder${side}`];
        const away = sh.x - j.chest.x;
        const out1 = Math.abs(away) < 1 ? -1 : Math.sign(away);
        out.push({
          z: zOf(sk, `shoulder${side}`, 0.12),
          draw: (g) => {
            for (let r = 0; r <= len; r++) {
              const k = r / len;
              const { dx } = flutter(sk, k, r, 0.6);
              const cx = sh.x + out1 * (1 + r * 0.4) + dx;
              const hw = 3 + r * 0.18;
              const L = Math.round(cx - hw);
              const R = Math.round(cx + hw);
              const left = len - r;
              for (let x = L; x <= R; x++) {
                let c = x === (out1 > 0 ? R : L) || (r > 2 && (x - L) % 3 === 2) ? s : e.colour;
                if (left < 2) c = e.edge ?? s;
                px(g, x, Math.round(sh.y - 1 + r), 1, 1, c);
              }
            }
          },
        });
      }
      return out;
    },
  };
}

// ── wide hanging sleeves ──

function bellSleeves(e: Extra & { id: 'sleeves' }): Layer {
  const s = shade(e.colour, -0.13);
  const len = e.len ?? 7;
  return {
    id: 'flow:sleeves',
    label: EXTRA_LABELS.sleeves,
    pieces(sk) {
      const j = sk.joints;
      return SIDES.map((side): Piece => {
        const el = j[`elbow${side}`];
        const wr = j[`wrist${side}`];
        return {
          z: Math.max(el.z, wr.z) + 0.2,
          draw: (g) => {
            const n = Math.max(2, Math.ceil(Math.hypot(wr.x - el.x, wr.y - el.y)) + 1);
            const { dx: sway } = flutter(sk, 1, len, 0.5);
            const billow = clothOf(sk)?.billow ?? 0;
            for (let i = 0; i <= n; i++) {
              const t = 0.15 + (0.85 * i) / n;
              const x = el.x + (wr.x - el.x) * t;
              const y = el.y + (wr.y - el.y) * t;
              // Strikes swing the sleeves out (cloth.ts `billow`).
              const drop = 1 + t * (len * 0.8 + billow * 0.4);
              const hw = 1.5 + t * (3 + billow * 0.2);
              // The bell flares away from the body's middle.
              const flare = Math.sign(x - j.chest.x) * t * (3 + billow * 0.3);
              for (let r = 0; r <= drop; r++) {
                const k = r / drop;
                const xx = x + flare * k + sway * k * t;
                const yy = Math.round(y + r);
                const L = Math.round(xx - hw);
                const R = Math.round(xx + hw);
                const cuff = r >= Math.floor(drop);
                for (let c = L; c <= R; c++) {
                  // The cuff's corners rounded off.
                  if (cuff && (c === L || c === R)) continue;
                  px(g, c, yy, 1, 1, cuff ? e.trim : c === L || c === R ? s : e.colour);
                }
              }
            }
          },
        };
      });
    },
  };
}

export function extraLayer(e: Extra): Layer {
  return e.id === 'scarf' ? flyScarf(e) : e.id === 'drape' ? drape(e) : bellSleeves(e);
}
